import React, { useState, useEffect, useCallback } from "react";
import { ImageModifier } from "../../common/ImageModifier";
import { TextModifier } from "../../common/TextModifier";
import {
    Typography,
    Box,
    Checkbox,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    FormControlLabel,
    Autocomplete,
    TextField,
    Stack,
    Divider,
    ListItem
} from "@mui/material";
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { usePermissionContext } from "../../../contexts/ProviderProvider";

export const NewProductEQMConfig = ({
    universities,
    eqmConfig,
    setEQMConfig,
    rows,
    setRows,
    errorMessage,
    setErrorMessage,
    expanded,
    setExpanded,
    // Updated props for asset options - now arrays of objects
    fontOptions,
    backIconLinkOptions,
    frontIconLinkOptions,
    templateLinkOptions,
    compositeOptions,
    generalAssetByType,
    selectedSkuType
}) => {
    console.log("asdf eqmConfig: ", eqmConfig)

    const { views } = usePermissionContext();

    // State for conditional fields per university
    const [conditionalStates, setConditionalStates] = useState({});

    // Updated field order and mapping based on reference code
    const fieldOrder = [
        "front_icon_general_asset_id",
        "has_sport_text", // Conditional trigger
        "front_text_font_link_general_asset_id",
        "front_text_font_color",
        "front_verified",
        "has_back", // conditional trigger
        "has_template_file", // Conditional trigger
        "mockup_template_general_asset_id",
        "mockup_verified",
    ];

    const getAssetTypeByfieldId = (name) => {
        if (name === "front_icon_general_asset_id") {
            return "Front Asset Icon File";
        } else if (name === "back_icon_general_asset_id") {
            return "Back Asset Icon File";
        } else if (name === "mockup_template_general_asset_id") {
            return selectedSkuType === "Jersey" ? "Jersey Art File" : "Mockup Template File";
        } else if (name === "front_text_font_link_general_asset_id" || name === "back_text_font_general_asset_id") {
            return "Font File";
        } else if (name === "back_jersey_number_font_general_asset_id") {
            return "Composite Font File";
        } else {
            return null;
        }
    }

    const getThumbnailVariantForImage = (img) => {
        if (img.includes("imagedelivery")) {
            return img.replace("/original", "/thumbnail");
        }
        return img
    }

    const isValidHexCode = (hexCode) => {
        if (!hexCode) return false;
        const hexRegex = /^#?([0-9A-F]{3}){1,2}$/i;
        return hexRegex.test(hexCode);
    };

    const setEQMConfigFilter = useCallback((university, value, field) => {
        setErrorMessage("");
        setEQMConfig((currentConfig) => {
            const newConfig = { ...currentConfig };

            if (!newConfig[university]) {
                newConfig[university] = {};
            }

            if (value !== undefined && value !== null && value !== "") {
                newConfig[university] = { ...newConfig[university], [field]: value };
            } else {
                delete newConfig[university][field];
                if (Object.keys(newConfig[university]).length === 0) {
                    delete newConfig[university];
                }
            }
            console.log(" asdf config updated", currentConfig, newConfig)
            return newConfig;
        });
    }, [setEQMConfig]);

    // Batched update function for clearing multiple fields at once
    const clearBackFields = useCallback((university) => {
        setErrorMessage("");
        setEQMConfig((currentConfig) => {
            const newConfig = { ...currentConfig };

            if (!newConfig[university]) {
                newConfig[university] = {};
            }

            // Clear all back-related fields in a single update
            const fieldsToClear = [
                "back_text_font_color",
                "back_jersey_number_font_color",
                "back_number_outline_color",
                "back_icon_general_asset_id",
                "back_text_font_general_asset_id",
                "back_jersey_number_font_general_asset_id",
                "back_verified"
            ];

            fieldsToClear.forEach(field => {
                delete newConfig[university][field];
            });

            // Clean up empty university config
            if (Object.keys(newConfig[university]).length === 0) {
                delete newConfig[university];
            }

            console.log(" asdf back fields cleared", currentConfig, newConfig)
            return newConfig;
        });
    }, [setEQMConfig]);

    // Helper function to get options array for a specific field
    const getOptionsForField = (field) => {
        if (field === "front_icon_general_asset_id") {
            return frontIconLinkOptions || [];
        } else if (field === "back_icon_general_asset_id") {
            return backIconLinkOptions || [];
        } else if (field === "mockup_template_general_asset_id") {
            return templateLinkOptions || [];
        } else if (field === "front_text_font_link_general_asset_id" || field === "back_text_font_general_asset_id") {
            return fontOptions || [];
        } else if (field === "back_jersey_number_font_general_asset_id") {
            return compositeOptions || [];
        }
        return [];
    };

    const getAssetFromId = (field, assetId) => {

        if (!assetId || !generalAssetByType) return "";

        let assetType = "";
        if (field === "front_icon_general_asset_id") {
            assetType = "Front Asset Icon File";
        } else if (field === "back_icon_general_asset_id") {
            assetType = "Back Asset Icon File";
        } else if (field === "mockup_template_general_asset_id") {
            if (selectedSkuType == "Jersey") {
                assetType = "Jersey Art File";
            } else {
                assetType = "Mockup Template File";
            }
        } else if (
            field === "front_text_font_link_general_asset_id" ||
            field === "back_text_font_general_asset_id"
        ) {
            assetType = "Font File";
        } else if (field === "back_jersey_number_font_general_asset_id") {
            assetType = "Composite Font File";
        }

        if (assetType && generalAssetByType[assetType]) {
            // Find the asset name by ID
            return generalAssetByType?.[assetType]?.[assetId] // allow this to be undefined if the asset that is in this value is not in the suggested return type from the server
        }
        return "";
    };


    const getAssetNameFromId = (field, assetId) => {
        return getAssetFromId(field, assetId)?.name
    }

    const setConditionalState = (university, field, value) => {
        setConditionalStates(prev => ({
            ...prev,
            [university]: {
                ...prev[university],
                [field]: value
            }
        }));
    };

    const getConditionalState = (university, field) => {
        return conditionalStates[university]?.[field] || false;
    };

    const getColor = (university, row) => {
        const universityConfig = eqmConfig[university] || {};
        const colorText = universityConfig[row.name] !== undefined ? universityConfig[row.name] : row.value;
        if (!colorText) return
        return colorText.startsWith("#") ? colorText : "#" + colorText;
    };

    // Updated helper function to get URL for a given field and value
    const getCurrentImageURL = (university, fieldName) => {
        const universityConfig = eqmConfig[university] || {};
        const currentValue = universityConfig[fieldName] || "";

        if (currentValue) {
            const url = getURLForField(fieldName, currentValue);
            return url.replace("https://www.dropbox.com", "https://dl.dropboxusercontent.com");
        }

        return "";
    };

    // Updated function to handle the new data structure
    const getURLForField = (field, id) => {
        if (!id || !generalAssetByType) return "";

        const assetType = getAssetTypeByfieldId(field);
        const assetMap = generalAssetByType[assetType];

        if (!assetMap) return "";

        // Find the asset by ID in the integer-keyed map
        const assetEntry = Object.values(assetMap).find(
            (obj) => obj.idProductGeneralAssets === id
        );

        return assetEntry?.asset_url || "";
    };

    // Updated config options to match reference structure
    const configOptions = [
        { "label": "Front Asset Link", "type": "ImageText", "required": false, "name": "front_icon_general_asset_id", "position": 1 },
        { "label": "Front Asset Font Name", "type": "Text", "required": false, "name": "front_text_font_link_general_asset_id", "position": 2 },
        { "label": "Front Asset Text Font Color", "type": "Color", "required": false, "name": "front_text_font_color", "position": 3 },
        { "label": "Front Asset Verified", "type": "Boolean", "required": false, "name": "front_verified", "position": 4 },
        { "label": "Back Asset Text Font Color", "type": "Color", "required": false, "name": "back_text_font_color", "position": 5 },
        { "label": "Back Asset Jersey Number Font Color", "type": "Color", "required": false, "name": "back_jersey_number_font_color", "position": 6 },
        { "label": "Back Asset Jersey Number Outline Color", "type": "Color", "required": false, "name": "back_number_outline_color", "position": 7 },
        { "label": "Back Asset Icon Link", "type": "ImageText", "required": false, "name": "back_icon_general_asset_id", "position": 8 },
        { "label": "Back Asset Last Name Font", "type": "Text", "required": false, "name": "back_text_font_general_asset_id", "position": 9 },
        { "label": "Back Asset Jersey Number Font", "type": "CompositeText", "required": false, "name": "back_jersey_number_font_general_asset_id", "position": 10 },
        { "label": "Back Asset Verified", "type": "Boolean", "required": false, "name": "back_verified", "position": 11 },
        { "label": "Template File Link", "type": "ImageText", "required": true, "name": "mockup_template_general_asset_id", "position": 12 },
        { "label": "Mockup Verified", "type": "Boolean", "required": false, "name": "mockup_verified", "position": 13 },
    ];

    const getSectionTitle = (fieldKey) => {
        if (fieldKey === "front_icon_general_asset_id") return "Front Asset Configuration";
        if (fieldKey === "has_back") return "Back Asset Configuration";
        if (fieldKey === "has_template_file") return "Mockup Configuration";
        return null;
    };

    useEffect(() => {
        const tempSelectedField = configOptions.map((option) => ({
            name: option.name || '',
            label: option.label || '',
            type: option.type || '',
            value: option.value || "",
            required: option.required || false,
            position: option.position || 0
        }));
        setRows(tempSelectedField);
    }, []);

    // Initialize conditional states for universities - only run when universities change
    useEffect(() => {
        console.log("asdf eqmConfig initial: ", eqmConfig)
        const initialStates = {};
        universities.forEach(university => {
            const universityConfig = eqmConfig[university] || {};


            initialStates[university] = {
                hasSportTextOnFront: !!(universityConfig.front_text_font_link_general_asset_id || universityConfig.front_text_font_color),
                hasBackAssetIconLink: !!universityConfig.back_icon_general_asset_id,
                hasTemplateFileLink: !!universityConfig.mockup_template_general_asset_id,
                has_back: universityConfig.has_back !== undefined ? !!universityConfig.has_back : false
            };
        });
        setConditionalStates(initialStates);
    }, [universities]); // Remove eqmConfig from dependencies

    // Update conditional states when specific fields change, but preserve manual has_back changes
    useEffect(() => {
        setConditionalStates(prev => {
            const newStates = { ...prev };
            universities.forEach(university => {
                const universityConfig = eqmConfig[university] || {};
                if (newStates[university]) {
                    newStates[university] = {
                        ...newStates[university],
                        hasSportTextOnFront: !!(universityConfig.front_text_font_link_general_asset_id || universityConfig.front_text_font_color),
                        hasBackAssetIconLink: !!universityConfig.back_icon_general_asset_id,
                        hasTemplateFileLink: !!universityConfig.mockup_template_general_asset_id,
                        // Don't override has_back if it's already set - preserve manual changes
                    };
                }
            });
            return newStates;
        });
    }, [eqmConfig, universities]);

    const handleAccordionChange = (university) => () => {
        setExpanded((prev) => ({
            ...prev,
            [university]: !prev[university],
        }));
    };

    const renderColorField = (university, row) => (
        <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <TextModifier
                        label={row.label}
                        original={row.value}
                        value={(eqmConfig[university] || {})[row.name]}
                        setValue={(value) => {
                            setEQMConfigFilter(university, value, row.name);
                        }}
                        permissions={[views.EQMANAGER]}
                        required={row.required && !!expanded[university]}
                        name={`${university}-${row.name}`}
                    />
                </Box>
                {getColor(university, row) && (
                    <Box
                        sx={{
                            bgcolor: getColor(university, row),
                            width: 60,
                            height: 40,
                            border: isValidHexCode(getColor(university, row)) ? "2px solid" : "2px dashed",
                            borderRadius: 1,
                            flexShrink: 0,
                        }}
                    />
                )}
            </Box>
        </Box>
    );

    const renderTextField = (university, row) => {
        const currentId = (eqmConfig[university] || {})[row.name];
        const currentValue = getAssetNameFromId(row.name, currentId, generalAssetByType, selectedSkuType);
        const options = getOptionsForField(row.name);

        return (
            <Box sx={{ mb: 2 }}>
                <Autocomplete
                    freeSolo
                    options={options}
                    getOptionLabel={(option) => {
                        // Handle both string and object options
                        if (typeof option === 'string') {
                            return option;
                        }
                        return option?.tags || '';
                    }}
                    value={currentValue}
                    onChange={(event, newValue) => {
                        // Handle object selection
                        if (newValue && typeof newValue === 'object') {
                            setEQMConfigFilter(university, newValue.idProductGeneralAssets, row.name);
                        }
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={row.label}
                            variant="outlined"
                            required={row.required && !!expanded[university]}
                            fullWidth
                            name={`${university}-${row.name}`}
                        />
                    )}
                    renderOption={(props, option) => (
                        <ListItem
                            {...props}
                            key={option.idProductGeneralAssets}
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '2fr 3fr 75px',
                                alignItems: 'center',
                                paddingY: 1,
                                paddingX: 2,
                                gap: 2,
                                minHeight: '56px',
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                                {option.name}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                                {option.tags}
                            </Typography>
                        </ListItem>
                    )}
                />

            </Box>
        );
    };

    const renderCompositeTextField = (university, row) => {
        const currentId = (eqmConfig[university] || {})[row.name];
        const currentValue = getAssetNameFromId(row.name, currentId, generalAssetByType, selectedSkuType);
        const imageURL = getCurrentImageURL(university, row.name);
        const options = getOptionsForField(row.name);

        return (
            <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <Autocomplete
                            options={options}
                            getOptionLabel={(option) => {
                                // Handle both string and object options
                                if (typeof option === 'string') {
                                    return option;
                                }
                                return option?.tags || '';
                            }}
                            value={currentValue}
                            onChange={(event, newValue) => {
                                // Handle object selection
                                if (newValue && typeof newValue === 'object') {
                                    setEQMConfigFilter(university, newValue.idProductGeneralAssets, row.name);
                                }
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={row.label}
                                    variant="outlined"
                                    required={row.required && !!expanded[university]}
                                    fullWidth
                                    name={`${university}-${row.name}`}
                                />
                            )}
                            renderOption={(props, option) => (
                                <ListItem
                                    {...props}
                                    key={option.idProductGeneralAssets}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '2fr 3fr 75px',
                                        alignItems: 'center',
                                        paddingY: 1,
                                        paddingX: 2,
                                        gap: 2,
                                        minHeight: '56px',
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    >
                                        {option.name}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    >
                                        {option.tags}
                                    </Typography>
                                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                        <img
                                            src={getThumbnailVariantForImage(option.asset_url)}
                                            alt="asset"
                                            style={{
                                                height: '50px',
                                                objectFit: 'contain',
                                                border: '1px solid #ccc',
                                                borderRadius: '2px',
                                                padding: '2px',
                                                background: '#fff',
                                            }}
                                        />
                                    </Box>
                                </ListItem>
                            )}

                        />
                    </Box>
                    {imageURL && (
                        <Box sx={{ flexShrink: 0 }}>
                            <img
                                alt="Preview"
                                style={{
                                    height: "56px",
                                    width: "80px",
                                    objectFit: "contain",
                                    cursor: "pointer",
                                    border: "1px solid #ddd",
                                    borderRadius: "4px",
                                    padding: "4px"
                                }}
                                src={imageURL}
                                onClick={() => window.open(imageURL, "_blank")}
                            />
                        </Box>
                    )}
                </Box>
            </Box>
        );
    };

    const renderImageField = (university, row) => {
        const currentId = (eqmConfig[university] || {})[row.name];
        const currentValue = getAssetNameFromId(row.name, currentId, generalAssetByType, selectedSkuType);
        const imageURL = getCurrentImageURL(university, row.name);
        const options = getOptionsForField(row.name);

        return (
            <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <Autocomplete
                            options={options}
                            getOptionLabel={(option) => {
                                // Handle both string and object options
                                if (typeof option === 'string') {
                                    return option;
                                }
                                return option?.tags || '';
                            }}
                            value={currentValue}
                            onChange={(event, newValue) => {
                                // Handle object selection
                                if (newValue && typeof newValue === 'object') {
                                    setEQMConfigFilter(university, newValue.idProductGeneralAssets, row.name);
                                }
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={row.label}
                                    variant="outlined"
                                    required={row.required && !!expanded[university]}
                                    fullWidth
                                    name={`${university}-${row.name}`}
                                />
                            )}
                            renderOption={(props, option) => (
                                <ListItem
                                    {...props}
                                    key={option.idProductGeneralAssets}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '2fr 3fr 75px',
                                        alignItems: 'center',
                                        paddingY: 1,
                                        paddingX: 2,
                                        gap: 2,
                                        minHeight: '56px',
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    >
                                        {option.name}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    >
                                        {option.tags}
                                    </Typography>
                                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                        <img
                                            src={getThumbnailVariantForImage(option.asset_url)}
                                            alt="asset"
                                            style={{
                                                height: '50px',
                                                objectFit: 'contain',
                                                border: '1px solid #ccc',
                                                borderRadius: '2px',
                                                padding: '2px',
                                                background: '#fff',
                                            }}
                                        />
                                    </Box>
                                </ListItem>
                            )}
                        />
                    </Box>
                    {imageURL && (
                        <Box sx={{ flexShrink: 0 }}>
                            <img
                                alt="Preview"
                                style={{
                                    height: "56px",
                                    width: "80px",
                                    objectFit: "contain",
                                    cursor: "pointer",
                                    border: "1px solid #ddd",
                                    borderRadius: "4px",
                                    padding: "4px"
                                }}
                                src={imageURL}
                                onClick={() => window.open(imageURL, "_blank")}
                            />
                        </Box>
                    )}
                </Box>
            </Box>
        );
    };

    const renderBooleanField = (university, field) => {
        const universityConfig = eqmConfig[university] || {};
        const currentValue = universityConfig[field.name] !== undefined ? universityConfig[field.name] : field.value;

        return (
            <Box sx={{ mb: 2 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={Boolean(currentValue)}
                            onChange={(e) => {
                                setEQMConfigFilter(university, e.target.checked, field.name);
                            }}
                            name={`${university}-${field.name}`}
                        />
                    }
                    label={field.label}
                />
            </Box>
        );
    };

    const renderFieldFilter = (university, field) => {
        // Skip conditional fields that are handled separately
        if (["front_text_font_link_general_asset_id",
            "front_text_font_color",
            "back_icon_general_asset_id",
            "mockup_template_general_asset_id",
            "back_text_font_color",
            "back_jersey_number_font_color",
            "back_number_outline_color"].includes(field.name)) {
            return null;
        }
        return renderField(university, field)
    }

    const renderField = (university, field) => {

        if (field.type === "Color") {
            return renderColorField(university, field);
        } else if (field.type === "Text" || field.type === "CompositeFontText") {
            return renderTextField(university, field);
        } else if (field.type === "CompositeText") {
            return renderCompositeTextField(university, field);
        } else if (field.type === "ImageText") {
            return renderImageField(university, field);
        } else if (field.type === "Boolean") {
            return renderBooleanField(university, field);
        } else {
            return (
                <Box sx={{ mb: 2 }}>
                    <ImageModifier
                        label={field.label}
                        original={field.name}
                        value={(eqmConfig[university] || {})[field.label]}
                        setValue={(value) => {
                            setEQMConfigFilter(university, value, field.name);
                        }}
                        name={`${university}-${field.name}`}
                        permissions={[views.EQMANAGER]}
                    />
                </Box>
            );
        }
    };

    const renderConditionalSection = (university, fieldKey) => {
        if (fieldKey === "has_sport_text") {
            const hasSportText = getConditionalState(university, "hasSportTextOnFront");
            const universityConfig = eqmConfig[university] || {};

            const currentFrontIconId = (eqmConfig[university] || {})["front_text_font_link_general_asset_id"];
            const currentFrontIconValue = getAssetNameFromId("front_text_font_link_general_asset_id", currentFrontIconId, generalAssetByType, selectedSkuType);

            return (
                <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={hasSportText}
                                onChange={(e) => {
                                    setConditionalState(university, "hasSportTextOnFront", e.target.checked);
                                    // Clear values if unchecked
                                    if (!e.target.checked) {
                                        setEQMConfigFilter(university, "", "front_text_font_link_general_asset_id");
                                        setEQMConfigFilter(university, "", "front_text_font_color");
                                    }
                                }}
                            />
                        }
                        label="Has Sport Text on Front?"
                    />

                    {hasSportText && (
                        <Box sx={{ ml: 3, mt: 1.5, pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                            <Stack spacing={2}>
                                <Autocomplete
                                    freeSolo
                                    options={fontOptions}
                                    getOptionLabel={(option) => {
                                        // Handle both string and object options
                                        if (typeof option === 'string') {
                                            return option;
                                        }
                                        return option?.tags || '';
                                    }}
                                    value={currentFrontIconValue}
                                    onChange={(event, newValue) => {
                                        // Handle object selection
                                        if (newValue && typeof newValue === 'object') {
                                            setEQMConfigFilter(university, newValue.idProductGeneralAssets, "front_text_font_link_general_asset_id");
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Front Asset Font Name"
                                            variant="outlined"
                                            fullWidth
                                            name={`${university}-front_text_font_link_general_asset_id`}
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <ListItem
                                            {...props}
                                            key={option.idProductGeneralAssets}
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: '2fr 3fr 75px',
                                                alignItems: 'center',
                                                paddingY: 1,
                                                paddingX: 2,
                                                gap: 2,
                                                minHeight: '56px',
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                            >
                                                {option.name}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                            >
                                                {option.tags}
                                            </Typography>
                                        </ListItem>
                                    )}
                                />

                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                    <Box sx={{ flex: 1 }}>
                                        <TextModifier
                                            label="Front Asset Text Font Color"
                                            original=""
                                            value={universityConfig["front_text_font_color"]}
                                            setValue={(value) => setEQMConfigFilter(university, value, "front_text_font_color")}
                                            permissions={[views.EQMANAGER]}
                                            required={false}
                                            name={`${university}-front_text_font_color`}
                                        />
                                    </Box>
                                    {universityConfig["front_text_font_color"] && (
                                        <Box
                                            sx={{
                                                bgcolor: universityConfig["front_text_font_color"].startsWith("#") ?
                                                    universityConfig["front_text_font_color"] :
                                                    "#" + universityConfig["front_text_font_color"],
                                                width: 60,
                                                height: 40,
                                                border: isValidHexCode(universityConfig["front_text_font_color"]) ? "2px solid" : "2px dashed",
                                                borderRadius: 1,
                                                flexShrink: 0,
                                            }}
                                        />
                                    )}
                                </Box>
                            </Stack>
                        </Box>
                    )}
                </Box>
            );
        } else if (fieldKey === "has_back") {
            const hasBack = getConditionalState(university, "has_back");
            const universityConfig = eqmConfig[university] || {};
            const currentBackIconId = (eqmConfig[university] || {})["back_icon_general_asset_id"];
            const currentBackIconValue = getAssetNameFromId("back_icon_general_asset_id", currentBackIconId, generalAssetByType, selectedSkuType);

            return (
                <Box sx={{ mb: 4 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={hasBack}
                                onChange={(e) => {
                                    console.log("has_back", e.target.checked);
                                    const newValue = e.target.checked;
                                    setConditionalState(university, "has_back", newValue);

                                    // Clear all back-related fields if unchecked using batched update
                                    if (!newValue) {
                                        clearBackFields(university);
                                        // Also clear the conditional state for back asset icon link
                                        setConditionalState(university, "hasBackAssetIconLink", false);
                                    }
                                    // Store the has_back value in the config
                                    setEQMConfigFilter(university, newValue, "has_back");
                                }}
                            />
                        }
                        label="Has Back?"
                    />

                    {hasBack && (
                        <Box sx={{ ml: 3, mt: 1.5, pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                <Box sx={{ flex: 1 }}>
                                    {["back_text_font_color",
                                        "back_jersey_number_font_color",
                                        "back_number_outline_color",
                                        "back_icon_general_asset_id",
                                        "back_text_font_general_asset_id",
                                        "back_jersey_number_font_general_asset_id",
                                        "back_verified"].map(sectionKey => (
                                            renderField(university, rows.find(r => r.name == sectionKey))
                                        ))}
                                </Box>
                            </Box>
                        </Box>
                    )}
                </Box>
            );
        } else if (fieldKey === "has_template_file") {
            const hasTemplateFile = getConditionalState(university, "hasTemplateFileLink");
            const universityConfig = eqmConfig[university] || {};
            const currentTemplateId = (eqmConfig[university] || {})["mockup_template_general_asset_id"];
            const currentTemplateValue = getAssetNameFromId("mockup_template_general_asset_id", currentTemplateId, generalAssetByType, selectedSkuType);

            return (
                <Box sx={{ mb: 4 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={true}
                                disabled
                            />
                        }
                        label="Has Template File Link?"
                    />


                    <Box sx={{ ml: 3, mt: 1.5, pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Box sx={{ flex: 1 }}>
                                <Autocomplete
                                    freeSolo
                                    options={templateLinkOptions}
                                    getOptionLabel={(option) => {
                                        // Handle both string and object options
                                        if (typeof option === 'string') {
                                            return option;
                                        }
                                        return option?.tags || '';
                                    }}
                                    value={currentTemplateValue}

                                    onChange={(event, newValue) => {
                                        // Handle object selection
                                        if (newValue && typeof newValue === 'object') {
                                            setEQMConfigFilter(university, newValue.idProductGeneralAssets, "mockup_template_general_asset_id");
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Template File Link"
                                            variant="outlined"
                                            required={hasTemplateFile && !!expanded[university]}
                                            fullWidth
                                            name={`${university}-mockup_template_general_asset_id`}
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <ListItem
                                            {...props}
                                            key={option.idProductGeneralAssets}
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: '2fr 3fr 75px',
                                                alignItems: 'center',
                                                paddingY: 1,
                                                paddingX: 2,
                                                gap: 2,
                                                minHeight: '56px',
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                            >
                                                {option.name}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                            >
                                                {option.tags}
                                            </Typography>
                                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                <img
                                                    src={getThumbnailVariantForImage(option.asset_url)}
                                                    alt="asset"
                                                    style={{
                                                        height: '50px',
                                                        objectFit: 'contain',
                                                        border: '1px solid #ccc',
                                                        borderRadius: '2px',
                                                        padding: '2px',
                                                        background: '#fff',
                                                    }}
                                                />
                                            </Box>
                                        </ListItem>
                                    )}
                                />
                            </Box>
                            {getCurrentImageURL(university, "mockup_template_general_asset_id") && (
                                <Box sx={{ flexShrink: 0 }}>
                                    <img
                                        alt="Template Preview"
                                        style={{
                                            height: "56px",
                                            width: "80px",
                                            objectFit: "contain",
                                            cursor: "pointer",
                                            border: "1px solid #ddd",
                                            borderRadius: "4px",
                                            padding: "4px"
                                        }}
                                        src={getCurrentImageURL(university, "mockup_template_general_asset_id")}
                                        onClick={() => window.open(getCurrentImageURL(university, "mockup_template_general_asset_id"), "_blank")}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Box>

                </Box>
            );
        }

        return null;
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Typography variant="h6" gutterBottom>
                EQM Configuration
            </Typography>

            {errorMessage && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
                    {errorMessage}
                </Box>
            )}

            {universities.map((university) => (
                <Accordion
                    key={university}
                    expanded={expanded[university] || false}
                    onChange={handleAccordionChange(university)}
                    sx={{ mb: 2 }}
                >
                    <AccordionSummary expandIcon={<ArrowDownwardIcon />}>
                        <Typography variant="h6">{university}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box sx={{ p: 2 }}>
                            {fieldOrder.map((fieldName, index) => {
                                const sectionTitle = getSectionTitle(fieldName);
                                const field = rows.find(r => r.name === fieldName);

                                if (!field) {
                                    // Handle conditional sections
                                    if (["has_back", "has_sport_text", "has_back_icon", "has_template_file"].includes(fieldName)) {
                                        return (
                                            <>
                                                <Box key={fieldName} sx={{ mb: index === fieldOrder.length - 1 ? 0 : 2 }}>
                                                    {sectionTitle && (
                                                        <>
                                                            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1, mt: index === 0 ? 0 : 3 }}>
                                                                {sectionTitle}
                                                            </Typography>
                                                            <Divider sx={{ mb: 2 }} />
                                                        </>
                                                    )}
                                                    {renderConditionalSection(university, fieldName)}
                                                </Box>
                                            </>
                                        );
                                    }
                                    return null;
                                }

                                return (
                                    <>
                                        <Box key={fieldName} sx={{ mb: index === fieldOrder.length - 1 ? 0 : 2 }}>
                                            {sectionTitle && (
                                                <>
                                                    <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1, mt: index === 0 ? 0 : 3 }}>
                                                        {sectionTitle}
                                                    </Typography>
                                                    <Divider sx={{ mb: 2 }} />
                                                </>
                                            )}
                                            {renderFieldFilter(university, field)}
                                        </Box>
                                    </>
                                );
                            })}
                        </Box>
                    </AccordionDetails>
                </Accordion>
            ))}
        </Box>
    );
};

export default NewProductEQMConfig;
